import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { useTranslations } from "@/hooks/useTranslations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type StudentListDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId?: string;
  onContactSelected: (userId: string) => void;
};

type Student = {
  id: string;
  auth_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  avatar_url?: string;
  status: string;
};

export const StudentListDialog = ({ open, onOpenChange, channelId, onContactSelected }: StudentListDialogProps) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const { t } = useTranslations('messages');
  
  
  useEffect(() => {
    if (!user?.id || !open) return;
    
    const fetchStudents = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('students')
          .select('*')
          .eq('trainer_id', user.id)
          .eq('status', 'active');
        
        if (error) throw error;
        // Filtrar apenas alunos com auth_id (conta criada)
        const eligibleStudents = (data || []).filter(s => s.auth_id);
        
        setStudents(eligibleStudents);
        setFilteredStudents(eligibleStudents);
      } catch (error) {
        console.error('Error fetching students:', error);
        toast.error(t('errorLoadingStudents') || 'Erro ao carregar alunos.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudents();
  }, [user?.id, open]);
  
  // Filter students based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }
    
    const filtered = students.filter(student => 
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredStudents(filtered);
  }, [searchTerm, students]);
  
  const handleStartChat = async (student: Student) => {
    try {
      if (!user || !student.auth_id) {
        toast.error(t('missingStudentAccount') || 'Conta de aluno em falta.');
        return;
      }

      if (!student.auth_id) {
        toast.error(t('studentNeedsAccount') || 'Aluno precisa de conta.');
        return;
      }

      

      // Apenas chamar onContactSelected para atualizar a UI no MessagesPage
      onContactSelected(student.auth_id);
      
    } catch (error) {
      console.error('Error selecting chat:', error);
      toast.error(t('errorStartingChat') || 'Erro ao selecionar conversa.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('students')}</DialogTitle>
        </DialogHeader>
        
        <div className="relative mb-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('searchStudents')}
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              {t('loading')}
            </div>
          ) : filteredStudents.length > 0 ? (
            <div className="space-y-2">
              {filteredStudents.map((student) => (
                <div 
                  key={student.id} 
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted"
                >
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={student.avatar_url || ''} />
                      <AvatarFallback>
                        {student.first_name?.[0]}{student.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{student.first_name} {student.last_name}</p>
                      {student.email && <p className="text-xs text-muted-foreground">{student.email}</p>}
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleStartChat(student)}
                    disabled={!student.auth_id}
                    title={student.auth_id ? t('startChat') : t('accountRequired')}
                  >
                    {t('startChat')}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-destructive">
              {t('noEligibleStudents')}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
